import React, { useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { ButtonVariant, themeProps } from '../../styles/theme';
import { getShortTimeStr } from '../../utils/functions';
import useGlobalStyles from '../../styles/index';
import SensorIcon from '../../styles/icons/SensorIcon';
import { sensorTypeDetails } from '../../utils/sensorProperties';
import RSSILookup from '../SensorConfig/RSSILookup';
import { appSwitchBtnHandler } from '../../Shell/helpers';
import { SubSensorType, VisibleSubSensor } from '../../services/api';
import MobileAppIcon from '../../styles/icons/MobileAppIcon';
import { useAppDispatch, useAppSelector } from '../../state/store';
import { getBleSensors, getSensorsById } from '../../state/selectors';
import { NearBySensor } from '../../state/types';

enum SortType {
  rssi = 'rssi', // sensor RSSI to gateway
  distance = 'distance', // distance from user Device
}

const ExtendedSensorType = {
  ...SubSensorType,
  All: 'All',
} as const;

type SensorType = (typeof ExtendedSensorType)[keyof typeof ExtendedSensorType];

interface NewSensorsListProps {
  loading: boolean;
  newSensors: VisibleSubSensor[] | undefined;
  handleRefresh: () => void;
  setSelectedSensorId: (value: string) => void;
}

function NewSensorsList({
  loading,
  newSensors,
  handleRefresh,
  setSelectedSensorId,
}: NewSensorsListProps): JSX.Element {
  const theme = useTheme();
  const { classes: globalClasses } = useGlobalStyles();
  const dispatch = useAppDispatch();
  const sensorsById = useAppSelector(getSensorsById);
  const bleSensors = useAppSelector(getBleSensors);
  const [sortType, setSortType] = useState(SortType.distance);
  const [sensorType, setSensorType] = useState<SensorType>(ExtendedSensorType.All);

  // filter unconfigured sensors, as some visible sensors might have been configured in different location/floor
  const unconfiguredSensors = useMemo(() => {
    const sensors: VisibleSubSensor[] = [];
    newSensors?.forEach((sensor) => {
      const sensorDetail = sensorsById.get(sensor.suggested_id);
      if (!sensorDetail) sensors.push(sensor);
    });
    return sensors;
  }, [newSensors, sensorsById]);

  const allSensorTypes = unconfiguredSensors
    ?.map((sensor) => sensor.type)
    .filter((type, index, array) => array.indexOf(type) === index);

  const sortedSensors = useMemo(() => {
    let filteredSensors = unconfiguredSensors;
    if (sortType === SortType.distance) {
      // separate new sensors form ble sensors list in its received RSSI (distance) order
      const bleNewSensors: NearBySensor[] =
        bleSensors?.filter((bleSensor: NearBySensor) =>
          unconfiguredSensors?.some(
            (sensor: VisibleSubSensor) => sensor.suggested_id === bleSensor.id
          )
        ) ?? [];

      // create and array with its visible props
      const bleVisibleSensors: VisibleSubSensor[] = [];
      bleNewSensors.forEach((bleItem) => {
        const visibleProps = unconfiguredSensors?.find(
          (visibleItem) => visibleItem.suggested_id === bleItem.id
        );
        if (visibleProps) bleVisibleSensors.push(visibleProps);
      });

      // get remaining new sensors
      const remainingFromNewSensors =
        unconfiguredSensors?.filter(
          (newSensor) =>
            !bleSensors?.some((sensor: NearBySensor) => sensor.id === newSensor.suggested_id)
        ) ?? [];

      // Combine both arrays where blesensors with distance are in beginning
      filteredSensors = [...bleVisibleSensors, ...remainingFromNewSensors];
    }
    return filteredSensors;
  }, [bleSensors, unconfiguredSensors, sortType]);

  const filteredSensors = useMemo(() => {
    let sensors = sortedSensors;
    if (sensorType !== ExtendedSensorType.All)
      sensors = sortedSensors?.filter((sensor) => sensor.type === sensorType);
    return sensors;
  }, [sensorType, sortedSensors]);

  return (
    <>
      <Box sx={{ margin: '10px 0' }} className={globalClasses.spaceBetweenFlex}>
        <Box sx={{ display: 'flex' }}>
          <SensorIcon stroke={theme.palette.text.primary} style={{ alignSelf: 'center' }} />
          <Typography variant="h5" sx={{ marginLeft: '10px', alignSelf: 'center' }}>
            Install Sensor
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          variant={ButtonVariant.default}
          onClick={handleRefresh}
          style={{ padding: '5px 10px', border: 'none' }}
        >
          Refresh
        </Button>
      </Box>
      {unconfiguredSensors && unconfiguredSensors.length > 0 ? (
        <>
          <span style={{ fontSize: themeProps.textSize.small }}>
            Make sure you are in right location and press to select sensor from list below
          </span>
          <br />
          <Box className={globalClasses.spaceBetweenFlex}>
            <Select
              label="sensorType"
              value={sensorType}
              onChange={(e) => setSensorType(e.target.value as SensorType)}
              variant="standard"
              sx={{ width: '200px', height: '25px', padding: '0 5px', alignSelf: 'end' }}
            >
              <MenuItem value={ExtendedSensorType.All}>
                <Box sx={{ display: 'flex' }}>
                  <Box sx={{ alignSelf: 'center' }}>
                    <SensorIcon stroke={theme.palette.text.primary} />
                  </Box>
                  <Box sx={{ alignSelf: 'center', marginLeft: '10px' }}> All</Box>
                </Box>
              </MenuItem>
              {allSensorTypes?.map((type) => {
                const typeDetails = sensorTypeDetails[type];
                const Icon = typeDetails?.icon ?? SensorIcon;
                const label = typeDetails?.label ?? 'All';
                return (
                  <MenuItem value={type as SensorType}>
                    <Box sx={{ display: 'flex' }}>
                      <Box sx={{ alignSelf: 'center' }}>
                        <Icon />
                      </Box>
                      <Box sx={{ alignSelf: 'center', marginLeft: '10px' }}> {label}</Box>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
            <Box sx={{ alignSelf: 'center' }}>
              <Typography
                variant="h1"
                style={{ fontSize: themeProps.textSize.small }}
                sx={{ lineHeight: 2, padding: 0, textAlign: 'center' }}
              >
                Sort By
              </Typography>
              <ToggleButtonGroup size="small" value={SortType} color="primary" sx={{}}>
                <ToggleButton
                  value={SortType.rssi}
                  onClick={() => setSortType(SortType.rssi)}
                  selected={sortType === SortType.rssi}
                  sx={{
                    padding: '0 10px',
                    textTransform: 'capitalize',
                    fontSize: themeProps.textSize.small,
                  }}
                >
                  Signal
                </ToggleButton>
                <ToggleButton
                  value={SortType.distance}
                  onClick={() => setSortType(SortType.distance)}
                  selected={sortType === SortType.distance}
                  sx={{
                    padding: '0 10px',
                    textTransform: 'capitalize',
                    fontSize: themeProps.textSize.small,
                  }}
                >
                  Distance
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          <List style={{ background: 'none', padding: '0' }}>
            {filteredSensors?.map((sensor) => {
              const Icon = sensor?.type
                ? sensorTypeDetails[sensor.type]?.icon ?? SensorIcon
                : SensorIcon;
              const sensorId = sensor.suggested_id;
              const bleRSSI =
                bleSensors?.find((bleSensor) => bleSensor.id === sensorId)?.rssi ?? undefined;
              const distance = bleRSSI
                ? Number((10 ** ((-69 - bleRSSI) / (10 * 2))).toFixed(1))
                : undefined;

              return (
                <ListItem
                  key={sensorId}
                  onClick={() => setSelectedSensorId(sensorId)}
                  style={{
                    margin: '5px 0',
                    background: theme.palette.primary.main,
                    borderRadius: '8px',
                  }}
                >
                  <Grid container spacing={1} alignItems="center">
                    <Grid size={{ md: 1, sm: 1, xs: 1 }}>
                      <Icon />
                    </Grid>
                    <Grid size={{ md: 4, sm: 4, xs: 6 }}>
                      {sensorsById.get(sensorId)?.name ?? sensorId}
                    </Grid>
                    <Grid size={{ md: 2, sm: 2, xs: 2 }} className={`${globalClasses.unixTime}`}>
                      {getShortTimeStr(sensor.last_seen)}
                    </Grid>
                    <Grid size={{ md: 2, sm: 2, xs: 1 }}>
                      <RSSILookup RSSI={sensor.rssi} />
                    </Grid>
                    <Grid
                      size={{ md: 2, sm: 2, xs: 2 }}
                      className={globalClasses.unixTime}
                      sx={{ textAlign: 'right' }}
                    >
                      <p>{distance ? `${distance} m` : '-'}</p>
                    </Grid>
                  </Grid>
                </ListItem>
              );
            })}
          </List>
        </>
      ) : (
        !loading && <Alert severity="error">Nearby sensors not available</Alert>
      )}
      <Button
        color="secondary"
        variant={ButtonVariant.default}
        onClick={() => {
          setSelectedSensorId('');
          appSwitchBtnHandler(dispatch);
        }}
        style={{ width: '100%', margin: '1rem auto', border: 'none' }}
        startIcon={<MobileAppIcon style={{ fontSize: '32px' }} />}
      >
        Scan Only Mode
      </Button>
    </>
  );
}

export default NewSensorsList;
